/**
 * ProposalStickyBar - Barra fixa de ação da landing pública de propostas
 *
 * Mantém o valor total e o CTA sempre acessíveis: no topo em telas grandes
 * (após rolar o hero) e fixada no rodapé em telas pequenas.
 *
 * @version 1.0.0
 * RELEASE: 2026-08-11 v1.0.0 - Criação inicial (redesign premium da landing /p/:token)
 */

import React from 'react';
import { CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatCurrency } from './proposalFormat';

interface ProposalStickyBarProps {
  visible: boolean;
  tenantName: string | null;
  logoUrl: string | null;
  proposalTitle: string;
  total: number;
  currency: string;
  onAccept: () => void;
}

export function ProposalStickyBar({
  visible,
  tenantName,
  logoUrl,
  proposalTitle,
  total,
  currency,
  onAccept,
}: ProposalStickyBarProps) {
  return (
    <div
      className={`fixed inset-x-0 bottom-0 z-40 border-t bg-card/95 backdrop-blur transition-all duration-300 md:bottom-auto md:top-0 md:border-b md:border-t-0 ${
        visible
          ? 'pointer-events-auto translate-y-0 opacity-100'
          : 'pointer-events-none translate-y-4 opacity-0 md:-translate-y-4'
      }`}
    >
      <div className="mx-auto flex max-w-4xl items-center gap-3 px-4 py-3">
        {logoUrl ? (
          <img
            src={logoUrl}
            alt={`Logotipo de ${tenantName ?? 'fornecedor'}`}
            className="hidden h-8 w-8 rounded-lg object-contain sm:block"
            loading="lazy"
          />
        ) : null}

        <div className="hidden min-w-0 flex-1 sm:block">
          <p className="truncate text-sm font-medium">{proposalTitle}</p>
          <p className="truncate text-xs text-muted-foreground">{tenantName}</p>
        </div>

        <div className="flex-1 sm:flex-none sm:text-right">
          <p className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Total</p>
          <p className="text-base font-semibold leading-tight">
            {formatCurrency(total, currency)}
          </p>
        </div>

        <Button onClick={onAccept} className="h-10 rounded-xl px-5 font-semibold">
          <CheckCircle2 className="mr-2 h-4 w-4" />
          Aceitar
        </Button>
      </div>
    </div>
  );
}
